import React, { useState, useEffect, useMemo, useCallback } from 'react';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import { utils, writeFile } from 'xlsx';
import { APP_API_URL, APP_NAME } from '../../utils';
import axios from 'axios';
import formatNumber, { formatDateWithTime } from '../../utils/functions';
import { useSelector } from 'react-redux';
import { Link, useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import OrderLayout from '../../layout/order-layout';
import { stageOngoingSaleForCart } from './cart-edit';

const SortIcon = ({ active, direction }) => (
  <span className="ms-1 opacity-75">{active ? (direction === 'asc' ? '↑' : '↓') : '↕'}</span>
);

const getClientName = (item) =>
  item?.client?.nom || item?.commande?.customer?.name || item?.commande?.customer?.nom || '—';

const getSortValue = (item, key) => {
  if (key === 'designation') return item?.commande?.designation ?? '';
  if (key === 'client') return getClientName(item);
  if (key === 'created_at') return item?.commande?.created_at ?? '';
  if (key === 'total_prix') return Number(item?.total_prix ?? 0);
  if (key === 'statut') return item?.commande?.statut_commande ?? '';
  return '';
};

export default function OrderValidation() {
  const navigate = useNavigate();
  const user = useSelector((state) => state.auth.user);

  const [data, setData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchInput, setSearchInput] = useState('');
  const [appliedFilters, setAppliedFilters] = useState({ search: '', date: '' });
  const [draftFilters, setDraftFilters] = useState({ date: '' });
  const [currentPage, setCurrentPage] = useState(1);
  const [perPage] = useState(10);
  const [sortConfig, setSortConfig] = useState({ key: 'created_at', direction: 'desc' });
  const [filtersOpen, setFiltersOpen] = useState(false);

  const fetchData = useCallback(async () => {
    if (!user?.id) return;
    setIsLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${APP_API_URL}/ongoing/commandes/${user.id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setData(response.data.commandes || []);
    } catch (error) {
      if (error.response?.status === 404) {
        setData([]);
      } else {
        console.error('Erreur lors de la récupération des commandes :', error);
        setData([]);
      }
    } finally {
      setIsLoading(false);
    }
  }, [user?.id]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setAppliedFilters((prev) => ({ ...prev, search: searchInput }));
      setCurrentPage(1);
    }, 400);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const filteredItems = useMemo(() => {
    return (data || []).filter((item) => {
      const search = appliedFilters.search.trim().toLowerCase();
      if (search) {
        const haystack = [
          item?.commande?.designation,
          getClientName(item),
          item?.commande?.statut_commande,
        ]
          .filter(Boolean)
          .join(' ')
          .toLowerCase();
        if (!haystack.includes(search)) return false;
      }

      if (appliedFilters.date && item?.commande?.created_at) {
        if (
          new Date(item.commande.created_at).toDateString() !==
          new Date(appliedFilters.date).toDateString()
        ) {
          return false;
        }
      }

      return true;
    });
  }, [data, appliedFilters]);

  const sortedItems = useMemo(() => {
    const sorted = [...filteredItems];
    const { key, direction } = sortConfig;
    const dir = direction === 'asc' ? 1 : -1;
    sorted.sort((a, b) => {
      const aValue = getSortValue(a, key);
      const bValue = getSortValue(b, key);
      if (aValue < bValue) return -1 * dir;
      if (aValue > bValue) return 1 * dir;
      return 0;
    });
    return sorted;
  }, [filteredItems, sortConfig]);

  const stats = useMemo(
    () => ({
      total: filteredItems.length,
      montant: filteredItems.reduce((sum, item) => sum + Number(item?.total_prix ?? 0), 0),
      lignes: filteredItems.reduce((sum, item) => sum + Number(item?.total_quantite ?? 0), 0),
    }),
    [filteredItems]
  );

  const totalPages = Math.max(1, Math.ceil(sortedItems.length / perPage));
  const indexOfLast = currentPage * perPage;
  const indexOfFirst = indexOfLast - perPage;
  const currentItems = sortedItems.slice(indexOfFirst, indexOfLast);

  const handlePageChange = (direction) => {
    if (direction === 'next' && currentPage < totalPages) setCurrentPage((p) => p + 1);
    else if (direction === 'prev' && currentPage > 1) setCurrentPage((p) => p - 1);
  };

  const requestSort = (key) => {
    setSortConfig((prev) => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc',
    }));
    setCurrentPage(1);
  };

  const isSortActive = (key) => sortConfig.key === key;

  const handleApplyFilters = (e) => {
    e.preventDefault();
    setAppliedFilters((prev) => ({ ...prev, date: draftFilters.date }));
    setCurrentPage(1);
  };

  const handleResetFilters = () => {
    setSearchInput('');
    setDraftFilters({ date: '' });
    setAppliedFilters({ search: '', date: '' });
    setCurrentPage(1);
    setSortConfig({ key: 'created_at', direction: 'desc' });
  };

  const handleRefresh = async () => {
    await fetchData();
    setCurrentPage(1);
  };

  const editInCart = async (item) => {
    try {
      await stageOngoingSaleForCart(item?.commande?.id);
      navigate('/commandes');
    } catch (error) {
      Swal.fire({
        icon: error.code === 'not_pending' ? 'info' : 'error',
        title: error.code === 'not_pending' ? 'Commande déjà traitée' : 'Chargement impossible',
        text: error.code ? error.message : 'La commande n\'a pas pu être ouverte dans le panier.',
      });
    }
  };

  const exportRows = currentItems.map((item) => [
    item?.commande?.designation ?? '—',
    getClientName(item),
    formatDateWithTime(item?.commande?.created_at),
    `${formatNumber(item?.total_prix ?? 0)} FCFA`,
    item?.commande?.statut_commande ?? '—',
  ]);

  const exportToPDF = () => {
    const doc = new jsPDF();
    doc.text('Validations des commandes', 14, 16);
    doc.autoTable({
      head: [['Référence', 'Client', 'Date', 'Montant', 'Statut']],
      body: exportRows,
      startY: 24,
    });
    doc.save(`Validations ${APP_NAME}.pdf`);
  };

  const exportToExcel = () => {
    const worksheet = utils.json_to_sheet(
      currentItems.map((item) => ({
        Référence: item?.commande?.designation,
        Client: getClientName(item),
        Date: formatDateWithTime(item?.commande?.created_at),
        Montant: item?.total_prix,
        Statut: item?.commande?.statut_commande,
      }))
    );
    const workbook = utils.book_new();
    utils.book_append_sheet(workbook, worksheet, 'Validations');
    writeFile(workbook, `Validations ${APP_NAME}.xlsx`);
  };

  return (
    <OrderLayout>
      <div className="duo-page duo-page--caisse-list">
        <div className="duo-hero duo-hero--compact mb-3">
          <p className="duo-hero-sub mb-1">Caisse</p>
          <h1 className="duo-hero-title">Validations des commandes</h1>
          <p className="duo-hero-sub mt-2 mb-0">
            Commandes en attente de validation — ouvrez une ligne pour valider.
          </p>
        </div>

        <div className="duo-kpi-row">
          <div className="duo-kpi-chip">
            <p className="duo-kpi-chip__label">En attente</p>
            <p className="duo-kpi-chip__value">{stats.total}</p>
          </div>
          <div className="duo-kpi-chip">
            <p className="duo-kpi-chip__label">Montant total</p>
            <p className="duo-kpi-chip__value">{formatNumber(stats.montant)} FCFA</p>
          </div>
          <div className="duo-kpi-chip">
            <p className="duo-kpi-chip__label">Quantité lignes</p>
            <p className="duo-kpi-chip__value">{stats.lignes}</p>
          </div>
        </div>

        <section className="duo-panel">
          <div className="duo-toolbar">
            <span className="fw-bold" style={{ color: '#3c3c3c', fontSize: '0.9rem' }}>
              Commandes à valider
            </span>
            <div className="duo-toolbar__actions">
              <button type="button" title="PDF" onClick={exportToPDF} className="duo-btn duo-btn--ghost duo-btn--icon" disabled={!currentItems.length}>
                <i className="bi bi-filetype-pdf" />
              </button>
              <button type="button" title="Excel" onClick={exportToExcel} className="duo-btn duo-btn--ghost duo-btn--icon" disabled={!currentItems.length}>
                <i className="bi bi-filetype-xls" />
              </button>
              <button type="button" title="Imprimer" onClick={() => window.print()} className="duo-btn duo-btn--ghost duo-btn--icon" disabled={!currentItems.length}>
                <i className="bi bi-printer" />
              </button>
              <button type="button" title="Rafraîchir" onClick={handleRefresh} className="duo-btn duo-btn--ghost duo-btn--icon">
                <i className="bi bi-arrow-clockwise" />
              </button>
              <button
                type="button"
                title="Filtres"
                onClick={() => setFiltersOpen(!filtersOpen)}
                className={`duo-btn duo-btn--ghost duo-btn--icon duo-btn--filter${filtersOpen ? ' is-open' : ''}`}
              >
                <i className={`bi ${filtersOpen ? 'bi-x-lg' : 'bi-funnel-fill'}`} />
              </button>
              <Link to="/commandes" className="duo-btn duo-btn--primary">
                <i className="bi bi-cash-stack" /> Caisse
              </Link>
            </div>
          </div>

          <div className="px-3 pt-2 pb-2 border-bottom" style={{ borderColor: '#f0f0f0' }}>
            <input
              type="search"
              className="duo-input"
              placeholder="Rechercher référence, client..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
            />
          </div>

          {filtersOpen && (
            <div className="duo-filters">
              <form onSubmit={handleApplyFilters}>
                <div className="duo-filters__grid">
                  <input
                    type="date"
                    className="duo-input"
                    title="Date de création"
                    value={draftFilters.date}
                    onChange={(e) => setDraftFilters({ date: e.target.value })}
                  />
                </div>
                <div className="d-flex justify-content-end gap-2">
                  <button type="button" className="duo-btn duo-btn--ghost" onClick={handleResetFilters}>
                    Réinitialiser
                  </button>
                  <button type="submit" className="duo-btn duo-btn--primary">
                    <i className="bi bi-search" /> Appliquer les filtres
                  </button>
                </div>
              </form>
            </div>
          )}

          <div className="duo-table-wrap">
            <table className="duo-table">
              <thead>
                <tr>
                  <th onClick={() => requestSort('designation')}>
                    Référence <SortIcon active={isSortActive('designation')} direction={sortConfig.direction} />
                  </th>
                  <th onClick={() => requestSort('client')}>
                    Client <SortIcon active={isSortActive('client')} direction={sortConfig.direction} />
                  </th>
                  <th onClick={() => requestSort('created_at')}>
                    Date <SortIcon active={isSortActive('created_at')} direction={sortConfig.direction} />
                  </th>
                  <th onClick={() => requestSort('total_prix')} className="text-end">
                    Montant <SortIcon active={isSortActive('total_prix')} direction={sortConfig.direction} />
                  </th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {isLoading ? (
                  <tr>
                    <td colSpan={5} className="duo-empty-row">
                      Chargement...
                    </td>
                  </tr>
                ) : currentItems.length > 0 ? (
                  currentItems.map((item) => (
                    <tr key={item.commande?.id ?? item.commande?.designation}>
                      <td className="fw-bold">{item.commande?.designation}</td>
                      <td>{getClientName(item)}</td>
                      <td className="text-nowrap">{formatDateWithTime(item.commande?.created_at)}</td>
                      <td className="text-end fw-bold" style={{ color: '#006633' }}>
                        {formatNumber(item.total_prix ?? 0)} FCFA
                      </td>
                      <td>
                        <div className="duo-table__actions">
                          <button
                            type="button"
                            className="duo-btn duo-btn--ghost duo-btn--sm"
                            title="Reprendre dans le panier"
                            onClick={() => editInCart(item)}
                          >
                            <i className="bi bi-cart" /> Modifier
                          </button>
                          <Link
                            to={`/commande/${item.commande?.designation}`}
                            className="duo-btn duo-btn--primary duo-btn--sm"
                            title="Valider"
                          >
                            <i className="bi bi-check2-square" /> Valider
                          </Link>
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={5} className="duo-empty-row">
                      {data.length === 0
                        ? 'Aucune commande en attente de validation.'
                        : 'Aucune commande ne correspond aux critères.'}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {!isLoading && sortedItems.length > 0 && (
            <div className="duo-footer-bar">
              <span>
                {indexOfFirst + 1} – {Math.min(indexOfLast, sortedItems.length)} sur {sortedItems.length}{' '}
                entrée{sortedItems.length > 1 ? 's' : ''}
              </span>
              <ul className="duo-pagination">
                <li>
                  <button
                    type="button"
                    className="duo-btn duo-btn--ghost"
                    disabled={currentPage === 1}
                    onClick={() => handlePageChange('prev')}
                  >
                    Précédent
                  </button>
                </li>
                <li>
                  <span className="duo-pagination__info px-2">
                    Page {currentPage} / {totalPages}
                  </span>
                </li>
                <li>
                  <button
                    type="button"
                    className="duo-btn duo-btn--primary"
                    disabled={currentPage >= totalPages}
                    onClick={() => handlePageChange('next')}
                  >
                    Suivant
                  </button>
                </li>
              </ul>
            </div>
          )}
        </section>
      </div>
    </OrderLayout>
  );
}
